import { Ear, Link2Off, Trash2 } from 'lucide-react';

import { Section } from '@/components/ui/Section';
import type { RevealStep, Spotlight } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorActions } from '@/store/editor.store';

import {
  applyClipWindow,
  clipBinding,
  currentWindow,
  removeClip,
  stepDurationMs,
  type ClipRef,
} from './timeline/clips';

interface Props {
  slideId: string;
  step: RevealStep | null;
  selectedClip?: ClipRef | null;
  onSelectClip?: (ref: ClipRef | null) => void;
}

/**
 * Per-step inspector: pause-for-input toggle plus an inventory of the step's
 * annotations and freehand strokes (with remove). When a timeline clip is
 * selected it also shows a Clip section to bind the clip's start/end to a spoken
 * word. Writes via upsertRevealStep.
 */
export function StepInspector({ slideId, step, selectedClip, onSelectClip }: Props) {
  const actions = useEditorActions();

  if (!step) {
    return (
      <div className="p-4 text-xs text-fg-subtle">Select a reveal step to inspect it.</div>
    );
  }

  const annotations = step.annotations ?? [];
  const strokes = step.drawStrokes ?? [];

  const patch = (next: Partial<RevealStep>) =>
    actions.upsertRevealStep(slideId, { ...step, ...next });

  // Only reveal/annotation/camera get the word-binding inspector panel; draw &
  // spotlight are timed purely on the timeline.
  const activeClip =
    selectedClip &&
    selectedClip.stepId === step.id &&
    (selectedClip.kind === 'reveal' || selectedClip.kind === 'annotation' || selectedClip.kind === 'camera')
      ? selectedClip
      : null;

  return (
    <div className="space-y-1">
      <Section title="Step">
        <input
          type="text"
          value={step.label ?? ''}
          onChange={(e) => patch({ label: e.target.value || undefined })}
          placeholder="Step name (e.g. Reveal the aorta)"
          className="w-full rounded-md border border-border px-2 py-1 text-[12px] text-fg"
        />
        <p className="mt-1 text-[10.5px] text-fg-subtle">
          Shown on the narration block in the timeline.
        </p>
      </Section>

      {activeClip && (
        <ClipSection
          slideId={slideId}
          step={step}
          clip={activeClip}
          onDeselect={() => onSelectClip?.(null)}
        />
      )}

      {selectedClip?.kind === 'spotlight' && selectedClip.stepId === step.id && (
        <SpotlightSection slideId={slideId} step={step} spotlightId={selectedClip.id} />
      )}

      <Section title="Interaction">
        <label className="flex items-center gap-2 text-sm text-fg">
          <input
            type="checkbox"
            checked={!!step.takeInput}
            onChange={(e) => patch({ takeInput: e.target.checked || undefined })}
          />
          <Ear size={14} className="text-fg-muted" />
          Pause &amp; listen for input after narration
        </label>
        <p className="mt-1 text-[10.5px] text-fg-subtle">
          The classroom waits for a student response (e.g. an MCQ answer) before
          advancing.
        </p>
      </Section>

      <Section title={`Annotations (${annotations.length})`}>
        {annotations.length === 0 ? (
          <p className="text-[11px] text-fg-subtle">
            None. Pick a tool above and click an element to add one.
          </p>
        ) : (
          <ul className="space-y-1">
            {annotations.map((a) => (
              <li
                key={a.id}
                className="flex items-center gap-2 rounded-md border border-border px-2 py-1 text-[12px]"
              >
                <span
                  className="h-3 w-3 rounded-sm"
                  style={{ background: a.color ?? '#facc15' }}
                />
                <span className="flex-1 capitalize">{a.kind}</span>
                <button
                  onClick={() => patch({ annotations: annotations.filter((x) => x.id !== a.id) })}
                  className="text-fg-subtle hover:text-red-500"
                  title="Remove annotation"
                >
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={`Drawings (${strokes.length})`}>
        {strokes.length === 0 ? (
          <p className="text-[11px] text-fg-subtle">
            None. Use the Pen or Highlighter tool to draw on the slide.
          </p>
        ) : (
          <ul className="space-y-1">
            {strokes.map((s) => (
              <li
                key={s.id}
                className="flex items-center gap-2 rounded-md border border-border px-2 py-1 text-[12px]"
              >
                <span className="h-3 w-3 rounded-full" style={{ background: s.color }} />
                <span className="flex-1">{s.points.length} pts</span>
                <button
                  onClick={() => patch({ drawStrokes: strokes.filter((x) => x.id !== s.id) })}
                  className="text-fg-subtle hover:text-red-500"
                  title="Remove stroke"
                >
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/**
 * Editor for the selected timeline clip: bind its start/end to a spoken word via
 * dropdowns (the precise way, complementing drag-snapping on the timeline), or
 * delete it. Mirrors the timeline's write path (applyClipWindow / removeClip).
 */
function ClipSection({
  slideId,
  step,
  clip,
  onDeselect,
}: {
  slideId: string;
  step: RevealStep;
  clip: ClipRef;
  onDeselect: () => void;
}) {
  const actions = useEditorActions();
  const words = step.audio?.words ?? [];
  const dur = stepDurationMs(step);
  const binding = clipBinding(step, clip.kind, clip.id);
  const cur = currentWindow(step, clip.kind, clip.id, dur);

  const kindLabel = clip.kind === 'reveal' ? 'Reveal' : clip.kind === 'annotation' ? 'Annotation' : 'Camera move';

  const setStartWord = (wordIndex: number | null) => {
    if (wordIndex == null) {
      actions.upsertRevealStep(
        slideId,
        applyClipWindow(step, clip.kind, clip.id, { ...cur, startMs: 0, startWordIndex: undefined }, dur),
      );
      return;
    }
    const w = words[wordIndex];
    if (!w) return;
    actions.upsertRevealStep(
      slideId,
      applyClipWindow(
        step,
        clip.kind,
        clip.id,
        { ...cur, startMs: Math.round(w.start * 1000), startWordIndex: wordIndex },
        dur,
      ),
    );
  };

  const startWordIndex = nearestWordIndex(words, cur.startMs, 'start');

  return (
    <Section title={`Clip · ${kindLabel}`}>
      {words.length === 0 ? (
        <p className="text-[11px] text-fg-subtle">
          This step has no per-word timing yet. Generate narration audio (with alignment) to bind
          clips to words. You can still drag/resize the clip on the timeline.
        </p>
      ) : (
        <div className="space-y-2">
          <label className="block">
            <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">
              Appears on word {binding.start && <span className="text-accent">· bound</span>}
            </span>
            <select
              value={startWordIndex ?? 0}
              onChange={(e) => setStartWord(Number(e.target.value))}
              className="w-full rounded-md border border-border px-2 py-1 text-[12px]"
            >
              {words.map((w, i) => (
                <option key={i} value={i}>
                  {i + 1}. {w.word} ({w.start.toFixed(2)}s)
                </option>
              ))}
            </select>
          </label>

          <p className="text-[10.5px] text-fg-subtle">
            The element appears on this word and stays for the rest of the slide. Drag its track on
            the timeline to re-time it.
          </p>

          <div className="flex items-center gap-2 pt-0.5">
            <button
              onClick={() =>
                actions.upsertRevealStep(
                  slideId,
                  applyClipWindow(step, clip.kind, clip.id, { startMs: 0, endMs: dur, openEnd: true }, dur),
                )
              }
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-fg-muted hover:bg-bg-subtle"
              title="Clear word binding (reset to full step)"
            >
              <Link2Off size={11} /> Clear
            </button>
            <button
              onClick={() => {
                actions.upsertRevealStep(slideId, removeClip(step, clip.kind, clip.id));
                onDeselect();
              }}
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-red-600 hover:bg-red-50"
            >
              <Trash2 size={11} /> Delete clip
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}

/** Editor for a selected spotlight: shape (circle/oval), size, and zoom. */
function SpotlightSection({
  slideId,
  step,
  spotlightId,
}: {
  slideId: string;
  step: RevealStep;
  spotlightId: string;
}) {
  const actions = useEditorActions();
  const sp = (step.spotlights ?? []).find((s) => s.id === spotlightId);
  if (!sp) return null;

  const patch = (next: Partial<Spotlight>) =>
    actions.upsertRevealStep(slideId, {
      ...step,
      spotlights: (step.spotlights ?? []).map((s) => (s.id === spotlightId ? { ...s, ...next } : s)),
    });
  const remove = () =>
    actions.upsertRevealStep(slideId, {
      ...step,
      spotlights: (step.spotlights ?? []).filter((s) => s.id !== spotlightId),
    });

  return (
    <Section title="Spotlight">
      <div className="space-y-2">
        <div className="flex gap-1.5">
          {(['circle', 'ellipse'] as const).map((shape) => (
            <button
              key={shape}
              onClick={() => patch({ shape })}
              className={cn(
                'flex-1 rounded-md border px-2 py-1 text-[11px] capitalize',
                sp.shape === shape ? 'border-accent bg-accent-soft text-accent' : 'border-border text-fg-muted',
              )}
            >
              {shape === 'ellipse' ? 'Oval' : 'Circle'}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-fg-subtle">Width</span>
            <input
              type="number"
              value={Math.round(sp.w)}
              onChange={(e) => patch({ w: Math.max(60, Number(e.target.value) || 60) })}
              className="w-full rounded-md border border-border px-2 py-1 text-[12px]"
            />
          </label>
          <label className="block">
            <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-fg-subtle">Height</span>
            <input
              type="number"
              value={Math.round(sp.h)}
              onChange={(e) => patch({ h: Math.max(60, Number(e.target.value) || 60) })}
              className="w-full rounded-md border border-border px-2 py-1 text-[12px]"
            />
          </label>
        </div>
        <label className="block">
          <span className="mb-0.5 flex items-center justify-between text-[10px] uppercase tracking-wider text-fg-subtle">
            <span>Zoom</span>
            <span>{(sp.zoom ?? 1.25).toFixed(2)}×</span>
          </span>
          <input
            type="range"
            min="1"
            max="2"
            step="0.05"
            value={sp.zoom ?? 1.25}
            onChange={(e) => patch({ zoom: Number(e.target.value) })}
            className="w-full"
          />
        </label>
        <label className="block">
          <span className="mb-0.5 flex items-center justify-between text-[10px] uppercase tracking-wider text-fg-subtle">
            <span>Ease in/out</span>
            <span>{((sp.easeMs ?? 700) / 1000).toFixed(1)}s</span>
          </span>
          <input
            type="range"
            min="200"
            max="2500"
            step="50"
            value={sp.easeMs ?? 700}
            onChange={(e) => patch({ easeMs: Number(e.target.value) })}
            className="w-full"
          />
        </label>
        <p className="text-[10.5px] text-fg-subtle">
          Click the slide with the Spotlight tool to reposition. Set its in/out time by dragging the
          clip on the timeline.
        </p>
        <button
          onClick={remove}
          className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-red-600 hover:bg-red-50"
        >
          <Trash2 size={11} /> Delete spotlight
        </button>
      </div>
    </Section>
  );
}

function nearestWordIndex(
  words: { start: number; end: number }[],
  ms: number,
  edge: 'start' | 'end',
): number | null {
  if (words.length === 0) return null;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < words.length; i++) {
    const boundary = (edge === 'start' ? words[i].start : words[i].end) * 1000;
    const dist = Math.abs(boundary - ms);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}
